import { Component, Input, Output, EventEmitter, OnChanges, OnDestroy, ViewChild, SimpleChanges } from '@angular/core';
import { UpdoxDirectMessage, ComposeDraft } from '../updox-direct-message';
import { UpdoxMessagesService } from '../updox-messages.service';
import { AlertService } from '../../../../_alert';
import { GlobalErrorHandlerService } from '../../../../services/global-error.service';
import { PatientAuthorizeSearchComponent } from '../../../../shared/patient-authorize-search/patient-authorize-search.component';
import { UpdoxAttachmentsComponent } from '../attachments/attachments.component';

@Component({
  selector: 'app-updox-message-detail',
  templateUrl: './message-detail.component.html',
})
export class UpdoxMessageDetailComponent implements OnChanges, OnDestroy {
  @Input() message: UpdoxDirectMessage;
  @Output() reply = new EventEmitter<ComposeDraft>();
  @Output() editDraft = new EventEmitter<UpdoxDirectMessage>();
  @Output() deleted = new EventEmitter<UpdoxDirectMessage>();

  // Always mounted (see template — [hideField], not *ngIf) so it's a stable
  // @ViewChild for the on-load verifyLinkedPatient() gate check, regardless
  // of whether the interactive search box is showing.
  @ViewChild('linkPatientSearch') linkPatientSearch: PatientAuthorizeSearchComponent;

  // Always mounted alongside the patient bar (see template) — read reactively
  // to gate unlinking, rather than mirroring its attachment list into a second
  // piece of state that could drift out of sync.
  @ViewChild(UpdoxAttachmentsComponent) attachmentsComponent: UpdoxAttachmentsComponent;

  isDeleting = false;
  showDeleteConfirm = false;
  isMarkingUnread = false;
  showAttachments = false;
  isLoadingBody = false;
  fullMessage: UpdoxDirectMessage | null = null;

  // Patient link bar
  isLinking = false;
  linkedPatientDetail: any = null; // { id, name, complete_detail } from link/verify response — richer than patient_name
  private linkMutatedLocally = false; // guards a slow getMessage response from clobbering a fresh link/unlink

  // Auto-detected candidate from a C-CDA attachment (see attachments.component's
  // ccdaPatientDetected) — offered as a one-click "Confirm" at the top instead
  // of making the user open Clinical Summary just to discover who it's about.
  ccdaCandidate: any = null;
  isConfirmingCcdaCandidate = false;
  // Raw parsed C-CDA demographics (first/last/dob/gender), kept even on a
  // miss so "Add New Patient" can open prefilled instead of blank, and so
  // the "no automatic match" hint knows there was actually a document to
  // check in the first place.
  ccdaDocPatient: any = null;

  // True from the moment a message opens until we know the patient bar's
  // *final* state — either "linked" (verified) or "not linked" (C-CDA check
  // settled, found or not). While true, the bar shows a plain "Checking…"
  // placeholder instead of committing to "No patient linked" and then having
  // that silently flip to a detected-candidate prompt seconds later, after
  // the body/attachments have already finished loading and the user has
  // moved on — the same area just stays visibly "still working" the whole
  // time instead of looking done and then surprising them.
  isCheckingPatient = false;
  private checkingPatientTimeoutId: any;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.message) {
      this.showAttachments   = false;
      this.showDeleteConfirm = false;
      this.fullMessage       = null;
      this.resetLinkState();

      if (this.message) {
        this.isLoadingBody = true;
        // Guard against a stale response landing after the user switched to
        // another message — it must not populate the newer message's view.
        const requestedId = this.message.id;

        if (!this.isDraft) {
          this.isCheckingPatient = true;
          // Safety net: whichever path is supposed to resolve this (see
          // verifyLinkedPatient() / onCcdaPatientDetected()) must not be able
          // to leave the bar looking "stuck checking" forever if something
          // upstream unexpectedly never calls back.
          this.checkingPatientTimeoutId = setTimeout(() => {
            if (this.message?.id === requestedId) { this.isCheckingPatient = false; }
          }, 6000);
        }

        this.messagesService.getMessage(requestedId)
          .then(json => {
            if (this.message?.id !== requestedId) { return; }
            this.isLoadingBody = false;
            this.fullMessage = new UpdoxDirectMessage().load_from_json(json);
            // The list payload may predate a link made elsewhere — trust the fresh
            // copy, unless the user just linked/unlinked while this request was in flight.
            if (!this.linkMutatedLocally) {
              this.message.patient_id = this.fullMessage.patient_id;
              this.message.patient_name = this.fullMessage.patient_name;
            }
            // A patient_id arriving from the backend (inbound message auto-match,
            // or a link made elsewhere) hasn't necessarily been authorized for
            // this practice yet — verify it silently, gating with SMS/KBA only
            // if it actually isn't linked. Deferred so #linkPatientSearch (a
            // @ViewChild) is guaranteed to exist by the time this runs, including
            // on this component's very first change (before ngAfterViewInit).
            const patientId = this.message.patient_id;
            if (patientId) {
              setTimeout(() => this.verifyLinkedPatient(requestedId, patientId), 0);
            } else {
              // Nothing to verify — the C-CDA auto-detect check (if any) is
              // what settles isCheckingPatient from here; see onCcdaPatientDetected().
            }
          })
          .catch(() => {
            if (this.message?.id !== requestedId) { return; }
            this.isLoadingBody = false;
            this.fullMessage = this.message;
            this.stopCheckingPatient(requestedId); // couldn't even confirm whether there's a patient_id to check
          });
      }
    }
  }

  // Enriches the linked-patient chip with full demographics once the practice's
  // authorization for patient_id is confirmed (silently if already linked, via
  // the SMS/KBA modal otherwise). Doesn't hide message.patient_id/patient_name
  // while pending — that metadata already came from the backend regardless —
  // it only gates the richer complete_detail from linkedPatientDetail.
  private verifyLinkedPatient(requestedId: number, patientId: number): void {
    if (!patientId || !this.linkPatientSearch) { this.stopCheckingPatient(requestedId); return; }
    this.linkPatientSearch.verifyPatientId(patientId)
      .then(patient => {
        if (this.message?.id !== requestedId) { return; } // stale — switched messages
        if (patient) { this.linkedPatientDetail = patient; }
        this.stopCheckingPatient(requestedId);
      });
  }

  private stopCheckingPatient(requestedId: number): void {
    if (this.message?.id !== requestedId) { return; } // stale — switched messages
    this.isCheckingPatient = false;
    clearTimeout(this.checkingPatientTimeoutId);
  }

  ngOnDestroy(): void {
    clearTimeout(this.checkingPatientTimeoutId);
  }

  private resetLinkState(): void {
    this.isLinking = false;
    this.linkedPatientDetail = null;
    this.linkMutatedLocally = false;
    this.ccdaCandidate = null;
    this.ccdaDocPatient = null;
    this.isConfirmingCcdaCandidate = false;
    this.isCheckingPatient = false;
    clearTimeout(this.checkingPatientTimeoutId);
    this.linkPatientSearch?.reset();
  }

  // --- C-CDA auto-detected candidate prompt ---

  // attachments.component always calls this once its background check is
  // settled — with the best-guess chart match (if any) and the document's
  // raw parsed demographics (if the C-CDA parsed at all) — specifically so
  // this is also the signal that lets isCheckingPatient stop, not just the
  // on-a-hit case.
  onCcdaPatientDetected(result: { matched: any; docPatient: any } | null): void {
    const candidate = result?.matched || null;
    // Ignore a genuine candidate if a patient has since been linked some
    // other way while the lookup was in flight (manual link, or a second
    // attachment's lookup already resolved first) — but still settle the
    // "Checking…" state either way.
    if (candidate && !this.message?.patient_id) {
      this.ccdaCandidate = candidate;
    }
    if (!candidate) {
      this.ccdaDocPatient = result?.docPatient || null;
    }
    if (this.message) { this.stopCheckingPatient(this.message.id); }
  }

  // Feeds the search box's "Add New Patient" fallback so, when the document
  // parsed but nothing in the chart matched, the form opens prefilled with
  // its demographics instead of blank. Raw HL7 gender code translated to
  // the form's values.
  get docPatientPrefill(): { first_name?: string; last_name?: string; dob?: string; gender?: string } {
    if (!this.ccdaDocPatient) { return null; }
    const g = this.ccdaDocPatient.gender;
    return {
      first_name: this.ccdaDocPatient.first_name,
      last_name:  this.ccdaDocPatient.last_name,
      dob:        this.ccdaDocPatient.dob,
      gender:     g === 'M' ? 'male' : (g === 'F' ? 'female' : ''),
    };
  }

  get ccdaNoMatchHint(): boolean {
    return !this.message?.patient_id && !this.isCheckingPatient && !this.ccdaCandidate && !!this.ccdaDocPatient;
  }

  confirmCcdaCandidate(): void {
    if (!this.ccdaCandidate || this.isConfirmingCcdaCandidate || !this.linkPatientSearch) { return; }
    this.isConfirmingCcdaCandidate = true;
    // The hint endpoint only matched on document demographics — it hasn't been
    // through the SMS/KBA authorization gate yet, unlike a search-box pick.
    this.linkPatientSearch.gatePatient(this.ccdaCandidate)
      .then(authorized => {
        this.isConfirmingCcdaCandidate = false;
        this.applyPatientLink(authorized);
      })
      .catch(() => {
        this.isConfirmingCcdaCandidate = false;
        // User cancelled the authorization modal — leave the prompt showing so they can retry.
      });
  }

  dismissCcdaCandidate(): void {
    this.ccdaCandidate = null;
  }

  get isDraft(): boolean {
    return this.message?.folder === 'draft';
  }

  get isInbound(): boolean {
    return this.message?.direction === 'inbound';
  }

  get deleteConfirmText(): string {
    if (this.message?.folder === 'deleted') {
      return 'This will permanently remove the message and cannot be undone.';
    }
    if (this.message?.folder === 'draft') {
      return 'This draft will be permanently deleted.';
    }
    return 'This message will be moved to Trash. You can find it in the Deleted folder.';
  }

  constructor(
    private messagesService: UpdoxMessagesService,
    private alertService: AlertService,
    private globalErrorHandler: GlobalErrorHandlerService,
  ) {}

  replyToMessage(): void {
    const original = this.fullMessage || this.message;
    const quotedDate = original.created_at
      ? new Date(original.created_at).toLocaleString()
      : '';
    const quotedBody = [
      '',
      '',
      `--- Original Message ---`,
      `From: ${original.from_address}`,
      quotedDate ? `Date: ${quotedDate}` : '',
      `Subject: ${original.subject}`,
      '',
      original.body_text || '',
    ].filter((line, i) => i < 4 || line !== '').join('\n');

    const draft: ComposeDraft = {
      localId:             Date.now(),
      to_address:          original.from_address,
      subject:             original.subject.startsWith('Re:')
                             ? original.subject
                             : `Re: ${original.subject}`,
      body_text:           quotedBody,
      reply_to_message_id: original.id,
      patient_id:          original.patient_id || undefined,
      minimized:           false,
      replyTo:             original,
    };
    this.reply.emit(draft);
  }

  markUnread(): void {
    if (this.isMarkingUnread) { return; }
    this.isMarkingUnread = true;
    this.messagesService.markRead(this.message.id, false)
      .then(() => {
        this.isMarkingUnread = false;
        this.message.read = false;
      })
      .catch(() => {
        this.isMarkingUnread = false;
        this.globalErrorHandler.error = 'Failed to mark message as unread.';
      });
  }

  confirmDelete(): void {
    this.showDeleteConfirm = true;
  }

  cancelDelete(): void {
    this.showDeleteConfirm = false;
  }

  // --- Patient link bar ---

  get patientDisplayName(): string {
    if (this.message?.patient_name) { return this.message.patient_name; }
    return this.message?.patient_id ? `Patient #${this.message.patient_id}` : '';
  }

  // Wired to the shared search component's (patientSelected) — it has already
  // authorization-gated the pick (same SMS/KBA flow as the on-load auto-check).
  selectLinkPatient(p: any): void {
    this.applyPatientLink(p);
  }

  // Persists the link and updates local state — shared by manual search
  // (already authorization-gated by the time it gets here) and
  // confirmCcdaCandidate() (gated just before calling this).
  private applyPatientLink(p: any): void {
    if (this.isLinking || !p?.id) { return; }
    this.isLinking = true;
    // Pin the target: if the user switches messages while the PUT is in flight,
    // the persisted link still belongs to this message — update its list row,
    // but leave the newly selected message's view alone.
    const msg = this.message;
    this.messagesService.linkPatient(msg.id, p.id)
      .then(resp => {
        // msg is shared with the list row — mutating it updates the chip too.
        msg.patient_id = resp?.patient_id || p.id;
        msg.patient_name = resp?.patient?.name || p.name || '';
        if (this.message === msg) {
          this.isLinking = false;
          this.linkMutatedLocally = true;
          this.linkedPatientDetail = resp?.patient || p;
          this.ccdaCandidate = null; // now resolved — the top prompt (if any) is no longer relevant
          this.ccdaDocPatient = null;
          this.linkPatientSearch?.reset();
        }
        this.alertService.success('Patient linked to message.', this.alertService.options);
      })
      .catch(resp => {
        if (this.message === msg) { this.isLinking = false; }
        this.globalErrorHandler.error = resp?.error?.error || 'Failed to link patient. Please try again.';
      });
  }

  // A patient can't be changed once any attachment has been filed to their
  // chart — that filing (Document, patient_id) already points at this
  // patient, so unlinking here would leave it orphaned/mismatched.
  get hasChartedAttachments(): boolean {
    return !!this.attachmentsComponent?.attachments?.some(a => a.added_to_medical_chart);
  }

  unlinkPatient(): void {
    if (this.isLinking || this.hasChartedAttachments) { return; }
    this.isLinking = true;
    const msg = this.message;
    this.messagesService.linkPatient(msg.id, null)
      .then(() => {
        msg.patient_id = null;
        msg.patient_name = '';
        if (this.message === msg) {
          this.isLinking = false;
          this.linkMutatedLocally = true;
          this.linkedPatientDetail = null;
        }
        this.alertService.success('Patient unlinked from message.', this.alertService.options);
      })
      .catch(resp => {
        if (this.message === msg) { this.isLinking = false; }
        this.globalErrorHandler.error = resp?.error?.error || 'Failed to unlink patient. Please try again.';
      });
  }

  patientDemographics(p: any): string {
    if (!p?.complete_detail) { return ''; }
    const dashIdx = p.complete_detail.lastIndexOf(' - ');
    const head = dashIdx >= 0 ? p.complete_detail.substring(0, dashIdx) : p.complete_detail;
    const commaIdx = head.indexOf(', ');
    return commaIdx >= 0 ? head.substring(commaIdx + 2) : '';
  }

  deleteMessage(): void {
    this.isDeleting = true;
    this.showDeleteConfirm = false;
    this.messagesService.deleteMessage(this.message.id)
      .then((resp: any) => {
        this.isDeleting = false;
        const toast = resp?.permanently_deleted
          ? 'Message permanently deleted.'
          : 'Message moved to Trash.';
        this.alertService.success(toast, this.alertService.options);
        this.deleted.emit(this.message);
      })
      .catch(() => {
        this.isDeleting = false;
        this.globalErrorHandler.error = 'Failed to delete message. Please try again.';
      });
  }
}
